import {useCallback, useMemo, useState} from "react";
import {type OnNodeDrag, type OnNodesChange, useReactFlow, type XYPosition} from "@xyflow/react";
import {notificationStore} from "~/stores/init";
import {NODE_HEIGHT, NODE_WIDTH, type TaxonomyTreeNode, type TaxonomyTreeNodeData} from "./taxonomyTreeLayout";
import type {TaxonomyIndex} from "./taxonomyTreeIndex";

/**
 * What dropping a category on a card would do:
 * - `ignore`: nothing (its own card or branch, or where it already is) — no highlight, no message
 * - `ok`: it can move there
 * - `blocked`: a sibling there already has its name (the check Add and Rename use)
 */
type DropVerdict = {kind: 'ignore'} | {kind: 'ok'} | {kind: 'blocked', message: string};

/**
 * The drop target is drawn about as large on screen as at 100% zoom: always at least a bit larger
 * than the others, and capped so that zoomed far out it doesn't cover half the tree.
 */
const MIN_TARGET_SCALE = 1.15;
const MAX_TARGET_SCALE = 3;
const targetScaleFor = (zoom: number) => Math.min(MAX_TARGET_SCALE, Math.max(MIN_TARGET_SCALE, 1 / zoom));

/** Whether a point is on the card, drawn `scale` times its size around its centre. */
const isOnCard = (n: TaxonomyTreeNode, x: number, y: number, scale = 1) => {
    const halfWidth = NODE_WIDTH * scale / 2;
    const halfHeight = NODE_HEIGHT * scale / 2;
    return Math.abs(x - (n.position.x + NODE_WIDTH / 2)) <= halfWidth
        && Math.abs(y - (n.position.y + NODE_HEIGHT / 2)) <= halfHeight;
};

type UseTaxonomyTreeDragMoveArgs = {
    /** The laid-out cards; only the non-root ones the user may edit should be `draggable`. */
    nodes: TaxonomyTreeNode[];
    index: TaxonomyIndex;
    rootId: number;
    /** Called when a category is dropped on a card it can move under. */
    onDrop: (id: number, toId: number) => void;
};

/**
 * Drag a card onto another card to move it (with its branch) under that card; the root card takes
 * it to L1. The dragged card follows the pointer; the card under its centre is the drop target,
 * outlined and enlarged while it is hovered. On release the card snaps back and `onDrop` asks for
 * confirmation. Any depth is allowed, as in the current builder.
 */
export function useTaxonomyTreeDragMove({nodes, index, rootId, onDrop}: UseTaxonomyTreeDragMoveArgs) {
    /** `overId`: the card currently under the dragged one; it keeps the target while over its enlarged size. */
    const [drag, setDrag] = useState<{id: string, position: XYPosition, overId?: string} | null>(null);
    const {getZoom} = useReactFlow();

    const verdict = useCallback((id: number, toId: number): DropVerdict => {
        const category = index.byId.get(id);
        if (!category) return {kind: 'ignore'};
        const parentId = category.parentId ?? rootId;
        if (toId === parentId || index.byId.get(toId)?.path.includes(id)) return {kind: 'ignore'};
        const name = category.label.toLowerCase();
        const taken = [...index.byId.values()].some(c => (c.parentId ?? rootId) === toId && c.label.toLowerCase() === name);
        if (!taken) return {kind: 'ok'};
        const under = toId === rootId ? 'L1' : index.byId.get(toId)?.label;
        return {kind: 'blocked', message: `A category named "${category.label}" already exists under ${under}.`};
    }, [index, rootId]);

    /**
     * The card under the dragged card's centre. A highlighted target counts at its enlarged size, so
     * it doesn't flicker on and off while the centre is over its grown edges.
     */
    const targetAt = useCallback((id: string, position: XYPosition, currentId?: string) => {
        const x = position.x + NODE_WIDTH / 2;
        const y = position.y + NODE_HEIGHT / 2;
        const current = currentId ? nodes.find(n => n.id === currentId) : undefined;
        if (current
            && verdict(Number(id), Number(current.id)).kind !== 'ignore'
            && isOnCard(current, x, y, targetScaleFor(getZoom()))) {
            return current;
        }
        return nodes.find(n => n.id !== id && isOnCard(n, x, y));
    }, [nodes, verdict, getZoom]);

    // Only the dragged card and the hovered target change, so the other cards keep their identity
    // and React Flow doesn't re-render them on every pointer move.
    const draggedNodes = useMemo((): TaxonomyTreeNode[] => {
        if (!drag) return nodes;
        const over = drag.overId ? nodes.find(n => n.id === drag.overId) : undefined;
        const result = over ? verdict(Number(drag.id), Number(over.id)) : undefined;
        const dropTarget: TaxonomyTreeNodeData['dropTarget'] =
            result?.kind === 'ok' ? 'valid' : result?.kind === 'blocked' ? 'invalid' : undefined;
        // Read on every pointer move, so zooming mid-drag resizes the target too.
        const dropTargetScale = targetScaleFor(getZoom());
        return nodes.map(n => {
            if (n.id === drag.id) return {...n, position: drag.position, zIndex: 1000};
            if (n === over && dropTarget) return {...n, zIndex: 999, data: {...n.data, dropTarget, dropTargetScale}};
            return n;
        });
    }, [nodes, drag, verdict, getZoom]);

    // The cards are laid out, not stored: follow only the dragged card's position changes.
    const onNodesChange = useCallback<OnNodesChange<TaxonomyTreeNode>>(changes => {
        for (const change of changes) {
            if (change.type === 'position' && change.dragging && change.position) {
                const {id, position} = change;
                setDrag(prev => ({id, position, overId: targetAt(id, position, prev?.overId)?.id}));
            }
        }
    }, [targetAt]);

    const onNodeDragStop = useCallback<OnNodeDrag<TaxonomyTreeNode>>((_, node) => {
        const over = targetAt(node.id, node.position, drag?.overId);
        setDrag(null);
        if (!over) return;
        const result = verdict(Number(node.id), Number(over.id));
        if (result.kind === 'ok') onDrop(Number(node.id), Number(over.id));
        if (result.kind === 'blocked') {
            notificationStore.show(result.message, {
                severity: 'error',
                id: `taxonomy-move-error-${node.id}`,
                autoHideDuration: 3000,
            });
        }
    }, [targetAt, verdict, onDrop, drag?.overId]);

    return {nodes: draggedNodes, onNodesChange, onNodeDragStop};
}
